/**
 * MIDI slot allocation for copied items (2026-08-09).
 *
 * THE ASK. Operator: *"if duplicating an item, the next available midi cc/
 * notes on same channel should be used"* - and, for when there isn't one,
 * *"prompt user in the inspector and have the item pulsate with a red aura"*.
 *
 * WHAT THIS REVERSES. `cloneItemWithNewIds` deliberately did NOT renumber CC,
 * on the reasoning that a silently-different CC is the worse surprise. That
 * held while the only caller was page-duplicate, where mirroring a whole page
 * onto the same CCs is usually the point. It does not hold for a single item:
 * duplicating a fader to sit next to its source and having both drive the
 * same Ableton parameter is never what anyone meant. So the ITEM-level copy
 * paths renumber here; `cloneItemWithNewIds` (and therefore page-duplicate)
 * is untouched.
 *
 * ONLY WHAT ACTUALLY COLLIDES MOVES. The rule is not "always renumber", it is
 * "renumber the slots that are already claimed". That one rule gives the two
 * behaviours the operator expects without a special case for either:
 *   * Duplicate - the source is still on the page, so every slot collides and
 *     the copy walks up to fresh numbers.
 *   * Cut then Paste - the source is gone, nothing collides, and the item
 *     comes back on the exact CC it left with, which is the whole point of a
 *     cut.
 *
 * SAME CHANNEL, ALWAYS. The search never leaves the source's channel, because
 * the channel is part of what the operator authored (and, for a pedal-bound
 * or Waves item, part of what the DEVICE dictates). Running out of room on
 * that channel is therefore a real, reportable outcome rather than something
 * to paper over by silently hopping to channel 4.
 *
 * SCOPE OF "CLAIMED" IS THE ACTIVE PAGE. Same domain as the layout linter's
 * `dup-cc` check (`performance/lint.ts`), deliberately: pages are alternate views of
 * one rig, so the same CC appearing on two pages is a normal authoring
 * choice, not a conflict. Allocating page-wide means a fresh duplicate never
 * raises a lint finding, and never jumps to CC 100 because page 4 happens to
 * use CC 12.
 *
 * WHAT DOESN'T PARTICIPATE, and why each is deliberate:
 *   * statusLed - it LISTENS. Two lamps echoing one CC is a legitimate rig,
 *     and an LED sharing a fader's CC is the intended use, not a clash.
 *   * presetChange / snapshotChange - device-fixed CCs on the device's own
 *     port. Same reasoning the linter spells out: this module has no concept
 *     of ports, so moving them would invent a conflict that cannot physically
 *     happen.
 *   * wavesAutotune - CC 55/56 are fixed by the plugin. A second Waves item
 *     needs a different CHANNEL, which is what `autotune/conflict.ts` already
 *     owns.
 *   * `pc` buttons - a program number is its own namespace with its own
 *     meaning (patch 7 IS patch 7); "next free" is not a sensible answer.
 */

import { createSignal } from 'solid-js';
import type { LayoutItem } from './projectStore.ts';

export type MidiSpace = 'cc' | 'note';

export interface MidiSlot {
  space: MidiSpace;
  /** 0-based, normalised. Item kinds disagree about this (waves stores
   *  1-based) so everything is converted on the way in, exactly as the
   *  linter does. */
  channel: number;
  number: number;
}

const MAX = 127;

const cc = (channel: number, number: number): MidiSlot => ({ space: 'cc', channel, number });
const note = (channel: number, number: number): MidiSlot => ({ space: 'note', channel, number });

function slotKey(s: MidiSlot): string {
  return `${s.space}:${s.channel}:${s.number}`;
}

/**
 * Every (space, channel, number) an item OCCUPIES. Shared by the occupancy
 * scan and the collision test, so the two can never disagree about what a
 * given kind claims.
 *
 * KEEP IN STEP WITH `performance/lint.ts`'s `ccClaims` - see the note there for why
 * they are two functions and what breaks when one gains a kind the other
 * doesn't.
 */
export function midiSlotsOf(item: LayoutItem): MidiSlot[] {
  switch (item.kind) {
    case 'button': {
      const actionType = item.actionType ?? 'cc';
      if (actionType === 'cc') return [cc(item.channel, item.ccNumber)];
      if (actionType === 'noteon') return [note(item.channel, item.note ?? 60)];
      return [];
    }
    case 'fader':
    case 'lfo':
    case 'radial':
      return [cc(item.channel, item.ccNumber)];
    case 'xypad':
      return [cc(item.xChannel, item.xCcNumber), cc(item.yChannel, item.yCcNumber)];
    case 'buttongrid':
      return item.cells.map((cell, i) =>
        cc(cell.channel ?? item.channel, cell.ccNumber ?? ((item.baseCC + i) & 0x7f)));
    case 'piano': {
      const base = (item.rootOctave + 2) * 12;
      const out: MidiSlot[] = [];
      for (let i = 0; i < item.octaves * 12; i++) {
        const n = base + i;
        if (n >= 0 && n <= MAX) out.push(note(item.channel, n));
      }
      return out;
    }
    case 'wavesAutotune': {
      const ch0 = (item.channel ?? 2) - 1; // waves stores 1-based
      return [cc(ch0, 55), cc(ch0, 56)];
    }
    default:
      return [];
  }
}

/** Occupancy set for a collection of items. */
export function takenSlots(items: readonly LayoutItem[]): Set<string> {
  const taken = new Set<string>();
  for (const it of items) for (const s of midiSlotsOf(it)) taken.add(slotKey(s));
  return taken;
}

/** First unclaimed number at or after `from`, wrapping once through 0..127.
 *  Null when the channel is genuinely full in that space. */
function firstFree(
  taken: Set<string>, space: MidiSpace, channel: number, from: number,
): number | null {
  for (let i = 0; i <= MAX; i++) {
    const n = (from + i) & 0x7f;
    if (!taken.has(`${space}:${channel}:${n}`)) return n;
  }
  return null;
}

/** First base for `size` CONSECUTIVE free CCs at or after `from`. Never wraps
 *  a block past 127 - a grid whose cells straddle the wrap would number
 *  126, 127, 0, 1, which reads as broken even though every number is free. */
function firstFreeBlock(
  taken: Set<string>, channel: number, from: number, size: number,
): number | null {
  for (let step = 0; step <= MAX; step++) {
    const base = (from + step) & 0x7f;
    if (base + size - 1 > MAX) continue;
    let ok = true;
    for (let i = 0; i < size; i++) {
      if (taken.has(`cc:${channel}:${base + i}`)) { ok = false; break; }
    }
    if (ok) return base;
  }
  return null;
}

const chLabel = (channel0: number) => `channel ${channel0 + 1}`;

export interface MidiRealloc {
  /** The item, with any colliding slots moved. Same object identity as the
   *  input when nothing had to move. */
  item: LayoutItem;
  /** Slots this call handed out. Empty when nothing moved. */
  allocated: MidiSlot[];
  /** Human sentence for the inspector, or null when the copy is clean. Set
   *  when at least one slot collided and had nowhere to go. */
  failure: string | null;
}

/**
 * Move whichever of `clone`'s MIDI slots are already claimed by `others` onto
 * the next free numbers on the SAME channel. Pure - returns a new item rather
 * than mutating, so the caller decides whether to commit.
 */
export function reallocateMidi(clone: LayoutItem, others: readonly LayoutItem[]): MidiRealloc {
  const taken = takenSlots(others);
  const allocated: MidiSlot[] = [];
  const failures: string[] = [];

  /** Mark a slot as spoken for WITHOUT counting it as newly handed out. Used
   *  for a slot the clone already held and got to keep - it still has to go
   *  into the set, or an XY pad's second axis could be handed its first
   *  axis's number, but it must not push the rolling CC counter (which the
   *  caller derives from `allocated`) past a number nothing new took. */
  const reserve = (s: MidiSlot): void => { taken.add(slotKey(s)); };
  /** Reserve + record as a fresh allocation. */
  const claim = (s: MidiSlot): void => { reserve(s); allocated.push(s); };
  const isTaken = (s: MidiSlot): boolean => taken.has(slotKey(s));

  switch (clone.kind) {
    case 'button': {
      const actionType = clone.actionType ?? 'cc';
      if (actionType === 'cc') {
        if (!isTaken(cc(clone.channel, clone.ccNumber))) break;
        const n = firstFree(taken, 'cc', clone.channel, clone.ccNumber + 1);
        if (n === null) { failures.push(`No free CC left on ${chLabel(clone.channel)}.`); break; }
        claim(cc(clone.channel, n));
        return { item: { ...clone, ccNumber: n }, allocated, failure: null };
      }
      if (actionType === 'noteon') {
        const current = clone.note ?? 60;
        if (!isTaken(note(clone.channel, current))) break;
        const n = firstFree(taken, 'note', clone.channel, current + 1);
        if (n === null) { failures.push(`No free note left on ${chLabel(clone.channel)}.`); break; }
        claim(note(clone.channel, n));
        return { item: { ...clone, note: n }, allocated, failure: null };
      }
      break; // pc - see the header note
    }

    case 'fader':
    case 'lfo':
    case 'radial': {
      if (!isTaken(cc(clone.channel, clone.ccNumber))) break;
      const n = firstFree(taken, 'cc', clone.channel, clone.ccNumber + 1);
      if (n === null) { failures.push(`No free CC left on ${chLabel(clone.channel)}.`); break; }
      claim(cc(clone.channel, n));
      return { item: { ...clone, ccNumber: n }, allocated, failure: null };
    }

    case 'xypad': {
      // The axes are allocated independently because they can legitimately sit
      // on different channels. Claiming X before searching for Y is what stops
      // a same-channel pad handing both axes the same fresh number.
      let xCcNumber = clone.xCcNumber;
      let yCcNumber = clone.yCcNumber;
      let moved = false;
      if (isTaken(cc(clone.xChannel, xCcNumber))) {
        const n = firstFree(taken, 'cc', clone.xChannel, xCcNumber + 1);
        if (n === null) failures.push(`No free CC left on ${chLabel(clone.xChannel)} for the X axis.`);
        else { xCcNumber = n; claim(cc(clone.xChannel, n)); moved = true; }
      } else {
        reserve(cc(clone.xChannel, xCcNumber));
      }
      if (isTaken(cc(clone.yChannel, yCcNumber))) {
        const n = firstFree(taken, 'cc', clone.yChannel, yCcNumber + 1);
        if (n === null) failures.push(`No free CC left on ${chLabel(clone.yChannel)} for the Y axis.`);
        else { yCcNumber = n; claim(cc(clone.yChannel, n)); moved = true; }
      }
      if (!moved) break;
      return {
        item: { ...clone, xCcNumber, yCcNumber },
        allocated,
        failure: failures.length ? failures.join(' ') : null,
      };
    }

    case 'buttongrid': {
      // Allocated as a BLOCK, not cell by cell, because the grid's whole
      // authoring model is "baseCC + index" - scattering its cells across the
      // gaps in a busy channel would produce a grid nobody could map.
      const size = clone.rows * clone.cols;
      const clash = clone.cells.some((cell, i) =>
        isTaken(cc(cell.channel ?? clone.channel, cell.ccNumber ?? ((clone.baseCC + i) & 0x7f))));
      if (!clash) break;
      const base = firstFreeBlock(taken, clone.channel, clone.baseCC + size, size);
      if (base === null) {
        failures.push(`No run of ${size} free CCs left on ${chLabel(clone.channel)}.`);
        break;
      }
      for (let i = 0; i < size; i++) claim(cc(clone.channel, base + i));
      // Per-cell overrides are deliberate author choices, so they survive the
      // rebase. They are also why the clash test reads through the override
      // rather than assuming baseCC + i.
      return { item: { ...clone, baseCC: base }, allocated, failure: null };
    }

    case 'piano': {
      // A piano claims a contiguous RANGE, so it moves by whole octaves  - 
      // anything else would leave the keyboard's C somewhere that isn't a C.
      const span = clone.octaves * 12;
      const fits = (root: number) => {
        const base = (root + 2) * 12;
        if (base < 0 || base + span - 1 > MAX) return false;
        for (let i = 0; i < span; i++) {
          if (taken.has(`note:${clone.channel}:${base + i}`)) return false;
        }
        return true;
      };
      if (fits(clone.rootOctave)) break;
      // Search upward from the source's octave first (the copy reads as "the
      // next one up"), then fall back to anything that fits.
      const candidates: number[] = [];
      for (let r = clone.rootOctave + 1; r <= 8; r++) candidates.push(r);
      for (let r = -2; r <= clone.rootOctave; r++) candidates.push(r);
      const root = candidates.find(fits);
      if (root === undefined) {
        failures.push(`No free ${clone.octaves}-octave note range left on ${chLabel(clone.channel)}.`);
        break;
      }
      const base = (root + 2) * 12;
      for (let i = 0; i < span; i++) claim(note(clone.channel, base + i));
      return { item: { ...clone, rootOctave: root }, allocated, failure: null };
    }

    default:
      break;
  }

  return {
    item: clone,
    allocated,
    failure: failures.length ? failures.join(' ') : null,
  };
}

// ─── Allocation-failure state (session-only) ─────────────────────────
//
// SESSION-ONLY ON PURPOSE, not an oversight. A persisted flag would have to
// be cleared by every path that can fix the problem (the inspector's CC
// field, the Matrix picker, deleting the item that was in the way, an undo, a
// bridge-driven restore) and any one of them missing it leaves a control
// pulsing red forever with nothing wrong. It is a report about something that
// JUST happened, so it lives as long as the session that saw it happen. The
// underlying collision is not lost on reload either: the item kept a
// colliding number, so `performance/lint.ts`'s `dup-cc` check still reports it, and
// pre-flight still surfaces it before a set.

const [failuresSig, setFailuresSig] = createSignal<Record<string, string>>({});

/** The reason this item could not be given a free slot, or null. */
export function midiAllocFailure(itemId: string): string | null {
  return failuresSig()[itemId] ?? null;
}

export function flagMidiAllocFailure(itemId: string, message: string): void {
  setFailuresSig({ ...failuresSig(), [itemId]: message });
}

/** Drop the flag - the item was edited, removed, or otherwise resolved. */
export function clearMidiAllocFailure(itemId: string): void {
  const current = failuresSig();
  if (!(itemId in current)) return;
  const { [itemId]: _drop, ...rest } = current;
  void _drop;
  setFailuresSig(rest);
}

/** Fields whose edit means the operator has answered the question the flag
 *  was asking. Kept as a list rather than "any edit at all" so moving or
 *  renaming a flagged item doesn't quietly clear a warning it hasn't fixed. */
const MIDI_FIELDS = [
  'ccNumber', 'channel', 'note', 'baseCC', 'rootOctave', 'octaves',
  'xCcNumber', 'yCcNumber', 'xChannel', 'yChannel', 'actionType', 'cells',
];

/** True when a patch touches anything the allocation failure was about. */
export function patchTouchesMidi(patch: object): boolean {
  return MIDI_FIELDS.some((f) => f in patch);
}
